import React, { useEffect, useState } from 'react';

const OrdersList = () => {
  const [orders, setOrders] = useState([]);
  const [filterDate, setFilterDate] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(false); // Added loading state
  const [completedCount, setCompletedCount] = useState(0);
  const [placedCount, setPlacedCount] = useState(0);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true); // Start loading
      try {
        const url = filterDate ? `https://api.ninjabakers.com/baker/api/orders?date=${filterDate}` : `https://api.ninjabakers.com/baker/api/orders`;
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          // Count order statuses
          const completed = data.filter(order => order.status === 'COMPLETED').length;
          const placed = data.filter(order => order.status === 'PLACED').length;

          setCompletedCount(completed);
          setPlacedCount(placed);

          const ordersWithImages = await Promise.all(
            data.map(async (order) => {
              const imageResponse = await fetch(`https://api.ninjabakers.com/baker/api/orders/image/${order.id}`);
              if (imageResponse.ok) {
                const imageBlob = await imageResponse.blob();
                order.imageSrc = URL.createObjectURL(imageBlob);
              }
              return order;
            })
          );
          setOrders(ordersWithImages);
        } else {
          console.error('Failed to fetch orders.');
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false); // Stop loading
      }
    };
    fetchOrders();
  }, [filterDate]);

  const markAsReady = async (id) => {
    try {
      const response = await fetch(`https://api.ninjabakers.com/baker/api/orders/${id}/ready`, { method: 'POST' });
      if (response.ok) {
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.id === id ? { ...order, status: 'COMPLETED' } : order
          )
        );
      } else {
        alert('Failed to mark as ready');
      }
    } catch (error) {
      console.error('Error marking order as ready:', error);
    }
  };

  const handleImageClick = (imageSrc) => {
    setRotation(0);
    setSelectedImage(imageSrc);
  };

  const rotateImage = () => {
    setRotation((prevRotation) => prevRotation + 90);
  };

  const closeModal = () => {
    setSelectedImage(null);
    setRotation(0);
  };

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Orders List</h1>


      {/* Order Summary Section */}
      <div className="bg-gray-100 p-4 mb-4 rounded-md shadow-md">
        <p className="text-lg font-semibold">Order Summary:</p>
        <p className="text-green-600 font-bold">✅ Completed Orders: {completedCount}</p>
        <p className="text-red-600 font-bold">🕒 Placed Orders: {placedCount}</p>
      </div>


      <div className="mb-4">
        <label className="mr-2 font-semibold">Filter by Date:</label>
        <input
          type="date"
          className="border px-2 py-1 rounded"
          value={filterDate ? new Date(filterDate).toISOString().split('T')[0] : ''}
          onChange={(e) => {
            const selectedDate = new Date(e.target.value);
            const formattedDate = `${selectedDate.getMonth() + 1}/${selectedDate.getDate()}/${selectedDate.getFullYear()}`;
            setFilterDate(formattedDate);
          }}
        />
      </div>

      {loading ? (
        <div className="text-center text-blue-500 text-lg font-semibold">Loading...</div>
      ) : (
        <table className="w-full border-collapse border">
          <thead>
            <tr className="bg-gray-200">
              <th className="border p-2">Receipt</th>
              <th className="border p-2">Date</th>
              <th className="border p-2">Time</th>
              <th className="border p-2">Status</th>
              <th className="border p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {orders.length > 0 ? (
              orders.map((order) => (
                <tr key={order.id} className="text-center border-t hover:bg-gray-50">
                  <td className="border p-2">
                    {order.imageSrc ? (
                      <img
                        src={order.imageSrc}
                        alt="Receipt"
                        className="h-20 w-20 object-cover cursor-pointer"
                        onClick={() => handleImageClick(order.imageSrc)}
                      />
                    ) : (
                      <span>No Image</span>
                    )}
                  </td>
                  <td className="border p-2">
                    {new Date(order.date).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                  </td>
                  <td className="border p-2">
                    {new Date(order.date).toLocaleTimeString('en-US', { timeZone: 'UTC' })}
                  </td>
                  <td className="border p-2">{order.status}</td>
                  <td className="border p-2">
                    {order.status !== 'COMPLETED' ? (
                      <button
                        className="bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
                        onClick={() => markAsReady(order.id)}
                      >
                        Mark as Ready
                      </button>
                    ) : (
                      <span className="text-green-600 font-bold">Completed</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center p-4">
                  No orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50">
          <div className="relative bg-white p-4 rounded-lg shadow-lg flex flex-col items-center">
            <div className="flex justify-end w-full">
              <button
                onClick={rotateImage}
                className="bg-yellow-400 px-3 py-1 rounded hover:bg-yellow-500 text-black mr-2 z-10"
              >
                Rotate
              </button>
              <button
                onClick={closeModal}
                className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 z-10"
              >
                Close
              </button>
            </div>
            <img
              src={selectedImage}
              alt="Full Receipt"
              style={{
                transform: `rotate(${rotation}deg)`,
                maxHeight: '80vh',
                maxWidth: '90vw',
                marginTop: '10px'
              }}
              className="shadow-lg rounded"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersList;